import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Button as MuiButton,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { Fragment, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useFormAccordo } from "./ContextAccordo";
import { Delete } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { useTheme } from "@mui/system";
import { NumericFormat } from "react-number-format";
import ConfermaElimina from "./lib/ConfermaElimina";
import AutocompleteSelect from "./lib/AutocompleteSelect";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/slices/snackbar";
import {
  FILTRO_CON_PARAMETRI,
  erroreFascia,
  erroriFascia,
  erroriOperazione,
} from "./lib/validaCondizioni";

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)});
`;

const Button = styled(MuiButton)`
  margin: 0 8px;
`;

const TIPI_SCONTO = [
  { label: "%", value: 1 },
  { label: "€", value: 2 },
];

// Colonne delle operazioni: Condizione, Parametri, Operazioni, Valore, Elimina
const operazioneGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "minmax(180px, 2fr) minmax(120px, 1fr) minmax(160px, 1.5fr) minmax(160px, 2fr) auto",
  },
  gap: 2,
  alignItems: "center",
};

const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

/* 
  Categorie: value 
  Operazioni: idOperazione && value
  Filtri: idTipologia === idOperazione && idCategoria === value in Categorie && value
  fasce: idFiltro === value in Filtri && idTipologia === value in Operazioni
*/

const Condizioni = () => {
  const { formState, dispatch } = useFormAccordo();
  const reduxDispatch = useDispatch();
  // Evidenzia i campi obbligatori vuoti dopo un'aggiunta o un salvataggio fallito
  const showErrors = !!formState.condizioniErrori;
  // Categorie, filtri e operazioni arrivano dall'api accordiOptions
  const { categorie, filtri, operazioni } = formState.options;
  const parametri = filtri.filter((el) => el.parametri?.length);
  const fasce = Array.isArray(formState.condizioni)
    ? formState.condizioni
    : Array.isArray(formState.condizioni?.dataset)
    ? formState.condizioni.dataset
    : [];

  const provenienza = formState.accordo?.idProvenienza;
  const provenienzaLabel = formState.options.provenienza.find(
    (el) => el.value === provenienza,
  )?.label;
  const provenienzaValori =
    formState.provenienzaValori?.[provenienzaLabel?.toLowerCase()];

  const [subTable, setSubTable] = useState();
  const [valueTabs, setValueTabs] = useState(0);
  const [rows, setRows] = useState(fasce);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [fasciaToDelete, setFasciaToDelete] = useState(null);

  const theme = useTheme();
  const slotProps = {
    paper: {
      sx: {
        backgroundColor: theme.palette.mode === "dark" ? "#1B2635" : "#F7F9FC",
      },
    },
  };

  // Filtri ordinati per categoria: l'autocomplete li raggruppa con groupBy
  const filtriPerCategoria = categorie.flatMap((categoria) =>
    filtri.filter((filtro) => filtro.idCategoria === categoria.value),
  );
  const categoriaFiltro = (filtro) =>
    categorie.find((categoria) => categoria.value === filtro.idCategoria)
      ?.label ?? "";

  const handleClick = (i) => {
    if (i === 0) {
      setSubTable(0);
    }
    if (subTable === i) {
      setSubTable("");
    } else {
      setSubTable(i);
    }
  };

  // Blocca l'aggiunta se le fasce indicate hanno campi obbligatori vuoti
  const verificaFasce = (fasceDaVerificare) => {
    const fasciaErrata = fasceDaVerificare.find((fascia) =>
      erroreFascia(fascia),
    );
    if (fasciaErrata) {
      dispatch({ type: "set_condizioni_errori", payload: true });
      setSubTable(fasciaErrata.idFascia);
      reduxDispatch(
        showSnackbar({
          message: erroreFascia(fasciaErrata),
          severity: "error",
        }),
      );
      return false;
    }
    dispatch({ type: "set_condizioni_errori", payload: false });
    return true;
  };

  const updateOperazione = (idFascia, index, patch) =>
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.idFascia === idFascia
          ? {
              ...row,
              operazioni: row.operazioni.map((operazione, i) =>
                i === index ? { ...operazione, ...patch } : operazione,
              ),
            }
          : row,
      ),
    );

  const handleAggiungiCondizione = (fascia) => {
    if (!verificaFasce(rows.filter((row) => row.idFascia === fascia))) return;
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.idFascia === fascia
          ? {
              ...row,
              operazioni: [
                ...row.operazioni,
                {
                  id: null,
                  // Fascia esistente: id della fascia; fascia nuova: null
                  idAccordoFascia: row.id ?? null,
                  isNew: 1,
                  parametro1: "",
                  idFiltro: 1,
                  idTipologia: 1,
                  condizione: "",
                },
              ],
            }
          : row,
      ),
    );
  };

  const handleAggiungiFascia = () => {
    if (!verificaFasce(rows)) return;
    setRows((prevRows) => [
      ...prevRows,
      {
        id: null,
        idFascia: prevRows.length, // Incremental fascia number
        operazioni: [],
        provenienzaIdValore: "",
        tipoSconto: 1,
        sconto: 0,
        isNew: 1,
      },
    ]);
  };

  const handleDelete = (type, index, subIndex = null) => {
    setRows((prevRows) => {
      if (type === "fascia") {
        // La fascia base non si elimina
        if (index === 0) return prevRows;
        // Rimuove la fascia e scala idFascia; l'id resta invariato
        return prevRows
          .filter((row) => row.idFascia !== index)
          .map((row, i) => ({ ...row, idFascia: i }));
      } else if (type === "condizione" && subIndex !== null) {
        // Only delete from the matching fascia; gli id non vengono toccati
        return prevRows.map((row) =>
          row.idFascia === index
            ? {
                ...row,
                operazioni: row.operazioni.filter((_, j) => j !== subIndex),
              }
            : row,
        );
      }
      return prevRows;
    });
  };

  // Callback for dialog confirmation
  const handleConfirmDelete = () => {
    if (fasciaToDelete !== null) {
      handleDelete("fascia", fasciaToDelete);
      setOpenDeleteDialog(false);
      setFasciaToDelete(null);
    }
  };

  useEffect(() => {
    console.log(rows);
    dispatch({
      type: "set_condizioni", // Replace with the appropriate action type
      payload: rows, // Pass the updated rows
    });
  }, [rows, dispatch]);

  useEffect(() => {
    // Assign fascia display order if missing
    setRows((prevRows) =>
      prevRows.map((row, i) =>
        row.idFascia === 0 ? { ...row, fascia: 0 } : { ...row, fascia: i },
      ),
    );
  }, []);
  console.log(rows);

  return (
    <>
      <ConfermaElimina
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
      <Box textAlign="end">
        <Button variant="contained" onClick={handleAggiungiFascia}>
          Nuova Fascia
        </Button>
      </Box>
      <Paper sx={{ mt: "0.5" }}>
        <TableWrapper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell align="left" colSpan={1}>
                  Fascia
                </TableCell>
                <TableCell align="center" colSpan={4}>
                  Impostazioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((fascia) => {
                const erroriF = showErrors ? erroriFascia(fascia) : {};
                return (
                  <Fragment key={fascia.idFascia}>
                    <TableRow
                      sx={
                        fascia.idFascia === 0
                          ? {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#1B2635"
                                  : "#F7F9FC",
                            }
                          : {}
                      }
                    >
                      <TableCell>
                        <IconButton
                          sx={{ mr: 4 }}
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleClick(fascia.idFascia)}
                        >
                          {subTable === fascia.idFascia ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <span style={{ fontWeight: "bold" }}>
                          {fascia.idFascia === 0 ? "Base" : fascia.idFascia}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        {fascia.idFascia !== 0 && (
                          <>
                            <AutocompleteSelect
                              id={`Provenienza/${fascia.idFascia}`}
                              label="Provenienza"
                              fullWidth={false}
                              sx={{ minWidth: 200 }}
                              slotProps={slotProps}
                              error={erroriF.provenienzaIdValore}
                              options={provenienzaValori ?? []}
                              getValue={(el) => el.ID}
                              getLabel={(el) => el.Valore}
                              value={fascia.provenienzaIdValore}
                              onChange={(value) =>
                                setRows((prevRows) =>
                                  prevRows.map((row) =>
                                    row.idFascia === fascia.idFascia
                                      ? { ...row, provenienzaIdValore: value }
                                      : row,
                                  ),
                                )
                              }
                            />
                          </>
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <Box display="flex" justifyContent="center" gap={2}>
                          {fascia.idFascia !== 0 && (
                            <>
                              <AutocompleteSelect
                                id={`TipoSC/${fascia.idFascia}`}
                                label="Tipo SC."
                                fullWidth={false}
                                sx={{ minWidth: 110 }}
                                slotProps={slotProps}
                                error={erroriF.tipoSconto}
                                options={TIPI_SCONTO}
                                value={fascia.tipoSconto}
                                onChange={(value) =>
                                  setRows((prevRows) =>
                                    prevRows.map((row) =>
                                      row.idFascia === fascia.idFascia
                                        ? { ...row, tipoSconto: value }
                                        : row,
                                    ),
                                  )
                                }
                              />
                            </>
                          )}
                          {fascia.idFascia !== 0 && (
                            <>
                              <NumericFormat
                                customInput={TextField}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                label="Sconto"
                                size="small"
                                margin="none"
                                sx={{ width: 120 }}
                                error={erroriF.sconto}
                                id={`Sconto/${fascia.idFascia}`}
                                name={`Sconto/${fascia.idFascia}`}
                                valueIsNumericString
                                value={fascia.sconto}
                                onValueChange={(e) =>
                                  setRows((prevRows) =>
                                    prevRows.map((row) =>
                                      row.idFascia === fascia.idFascia
                                        ? { ...row, sconto: e.value } // Toggle the bersani property
                                        : row,
                                    ),
                                  )
                                }
                              />
                            </>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        {fascia.idFascia !== 0 && (
                          <Tooltip title="Elimina Fascia">
                            <Button
                              variant="contained"
                              onClick={() => {
                                setFasciaToDelete(fascia.idFascia);
                                setOpenDeleteDialog(true);
                              }}
                            >
                              <Delete />
                            </Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    {/* SubTable Fasce */}
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1B2635" : "#F7F9FC",
                      }}
                    >
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={8}
                      >
                        <Collapse
                          in={subTable === fascia.idFascia}
                          timeout="auto"
                          unmountOnExit
                          sx={{ mb: 2 }}
                        >
                          <Tabs
                            value={valueTabs}
                            onChange={(e, newValue) => setValueTabs(newValue)}
                            sx={{ marginTop: "4px" }}
                          >
                            <Tab label="Condizioni" />
                          </Tabs>
                          <Card>
                            <CardContent>
                              <CustomTabPanel value={valueTabs} index={0}>
                                <Box
                                  sx={{
                                    margin: 1,
                                  }}
                                >
                                  <Box
                                    marginBottom={2}
                                    display="flex"
                                    justifyContent="end"
                                  >
                                    <Button
                                      variant="outlined"
                                      onClick={() =>
                                        handleAggiungiCondizione(
                                          fascia.idFascia,
                                        )
                                      }
                                    >
                                      Nuova Condizione
                                    </Button>
                                  </Box>
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={2}
                                  >
                                    {fascia.operazioni?.map((condizione, j) => {
                                      const errori = showErrors
                                        ? erroriOperazione(condizione)
                                        : {};
                                      const isTestuale =
                                        operazioni.find(
                                          (operazione) =>
                                            operazione.value ===
                                            condizione.idTipologia,
                                        )?.idOperazione === 1;
                                      return (
                                        <Box
                                          key={condizione.id ?? `new-${j}`}
                                          sx={operazioneGridSx}
                                        >
                                          <AutocompleteSelect
                                            id={`filtro/${fascia.idFascia}/${j}`}
                                            label="Condizione"
                                            slotProps={slotProps}
                                            error={errori.idFiltro}
                                            options={filtriPerCategoria}
                                            groupBy={categoriaFiltro}
                                            value={condizione.idFiltro}
                                            onChange={(value) =>
                                              updateOperazione(
                                                fascia.idFascia,
                                                j,
                                                {
                                                  idFiltro: value,
                                                  idTipologia: "",
                                                  parametro1: "",
                                                },
                                              )
                                            }
                                          />
                                          {condizione.idFiltro ===
                                          FILTRO_CON_PARAMETRI ? (
                                            <AutocompleteSelect
                                              id={`parametri/${fascia.idFascia}/${j}`}
                                              label="Parametri"
                                              slotProps={slotProps}
                                              error={errori.parametro1}
                                              options={
                                                parametri[0]?.parametri ?? []
                                              }
                                              value={condizione.parametro1}
                                              onChange={(value) =>
                                                updateOperazione(
                                                  fascia.idFascia,
                                                  j,
                                                  { parametro1: value },
                                                )
                                              }
                                            />
                                          ) : (
                                            // Mantiene l'allineamento delle colonne
                                            <Box
                                              sx={{
                                                display: {
                                                  xs: "none",
                                                  md: "block",
                                                },
                                              }}
                                            />
                                          )}
                                          <AutocompleteSelect
                                            id={`operazione/${fascia.idFascia}/${j}`}
                                            label="Operazioni"
                                            slotProps={slotProps}
                                            error={errori.idTipologia}
                                            options={operazioni.filter(
                                              (operazione) =>
                                                operazione.idOperazione ===
                                                filtri.find(
                                                  (filtro) =>
                                                    filtro.value ===
                                                    condizione.idFiltro,
                                                )?.idTipologia,
                                            )}
                                            value={condizione.idTipologia}
                                            onChange={(value) =>
                                              updateOperazione(
                                                fascia.idFascia,
                                                j,
                                                { idTipologia: value },
                                              )
                                            }
                                          />
                                          {isTestuale ? (
                                            <TextField
                                              size="small"
                                              margin="none"
                                              label="Valore"
                                              placeholder="Nuova Condizione"
                                              error={errori.condizione}
                                              value={condizione.condizione}
                                              id={`condizione/${fascia.idFascia}/${j}`}
                                              fullWidth
                                              onChange={(e) =>
                                                updateOperazione(
                                                  fascia.idFascia,
                                                  j,
                                                  {
                                                    condizione: e.target.value,
                                                  },
                                                )
                                              }
                                            />
                                          ) : (
                                            <NumericFormat
                                              customInput={TextField}
                                              size="small"
                                              margin="none"
                                              label="Valore"
                                              thousandSeparator="."
                                              fullWidth
                                              decimalSeparator=","
                                              id={`condizione/${fascia.idFascia}/${j}`}
                                              name={`condizione/${fascia.idFascia}/${j}`}
                                              valueIsNumericString
                                              error={errori.condizione}
                                              value={condizione.condizione}
                                              onValueChange={(e) =>
                                                updateOperazione(
                                                  fascia.idFascia,
                                                  j,
                                                  {
                                                    condizione: e.value,
                                                  },
                                                )
                                              }
                                            />
                                          )}
                                          <Tooltip title="Elimina Condizione">
                                            <Button
                                              variant="outlined"
                                              sx={{
                                                m: 0,
                                                justifySelf: {
                                                  xs: "end",
                                                  md: "auto",
                                                },
                                              }}
                                              onClick={() =>
                                                handleDelete(
                                                  "condizione",
                                                  fascia.idFascia,
                                                  j,
                                                )
                                              }
                                            >
                                              <Delete />
                                            </Button>
                                          </Tooltip>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              </CustomTabPanel>
                            </CardContent>
                          </Card>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color={showErrors ? "error" : "textSecondary"}>
                      Nessuna condizione presente. Aggiungi una fascia per
                      salvare l'accordo.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </Paper>
    </>
  );
};
export default Condizioni;
